// События карточек спонсоров на наш сервер (контракт C2, B1): POST sponsored/events/ {events: [{card_id, kind, placement}]},
// 1–50 событий в теле, ответ 202 {accepted}. По ним сервер считает показы и нажатия карточки за день (кабинет рекламодателя);
// gtag тут ни при чём — он может быть заблокирован, наш адрес — нет.
// - В очередь — только событие с числовым card_id от сервера (C3): поля C3 и ручка C2 выкатываются одним шагом, поэтому
//   без card_id в строке ручки ещё нет и в сеть не уходит ничего.
// - Отправка — через несколько секунд после первого события в очереди, сразу при полной пачке (50) и при сворачивании
//   (visibilitychange → hidden, keepalive: переживает закрытие окна).
// - Ошибка сервера или 429 — один повтор (429 — по Retry-After), потом пачка отбрасывается; 4xx кроме 429 — без повтора.
//   Нет сети — события остаются в очереди (предел 50, старые вытесняются) и уходят со следующей отправкой.
// - Ничего отсюда не бросает наружу и не влияет на экран.
import { ApiError, apiRequest } from './client';

const PATH = '/api/v5/mobile/sponsored/events/';
/** Предел тела запроса и очереди (C2). */
export const BATCH_LIMIT = 50;
/** Через сколько после первого события в очереди уходит пачка. */
export const FLUSH_DELAY_MS = 5_000;
/** Пауза перед повтором при 5xx и при 429 без Retry-After; Retry-After дольше предела — не ждём, пачку отбрасываем. */
const RETRY_DELAY_MS = 2_000;
const RETRY_AFTER_LIMIT_MS = 60_000;

export type SponsoredKind = 'view' | 'click';
export interface SponsoredEvent { card_id: number; kind: SponsoredKind; placement: string }

let queue: SponsoredEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let sending = false;

/** В очередь: только с числовым card_id сервера (C3). Без него — ничего (ручки на сервере ещё нет). */
export function queueSponsored(cardId: unknown, kind: SponsoredKind, placement: string): void {
  if (typeof cardId !== 'number' || !Number.isInteger(cardId) || cardId <= 0) return;
  queue.push({ card_id: cardId, kind, placement });
  if (queue.length > BATCH_LIMIT) queue = queue.slice(-BATCH_LIMIT);
  if (queue.length >= BATCH_LIMIT) { void flushSponsored(); return; }
  schedule();
}
function schedule(): void {
  if (!timer) timer = setTimeout(() => { timer = null; void flushSponsored(); }, FLUSH_DELAY_MS);
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Пауза перед единственным повтором; null — повторять нельзя (4xx, кроме 429; слишком долгий Retry-After). */
function retryDelay(error: unknown): number | null {
  if (!(error instanceof ApiError) || error.kind !== 'http') return null;
  if (error.status === 429) {
    const ms = (error.retryAfterSeconds ?? RETRY_DELAY_MS / 1000) * 1000;
    return ms <= RETRY_AFTER_LIMIT_MS ? ms : null;
  }
  return error.status >= 500 ? RETRY_DELAY_MS : null;
}

/** Одна пачка: true — ушла или отброшена после повтора; false — нет сети (вернуть в очередь). */
async function sendBatch(events: SponsoredEvent[], keepalive: boolean): Promise<boolean> {
  const post = () => apiRequest('POST', PATH, { body: { events }, keepalive });
  try {
    await post();
    return true;
  } catch (error) {
    if (error instanceof ApiError && error.kind === 'network') return false;
    const delay = retryDelay(error);
    if (delay === null) return true;
    await wait(delay);
    try { await post(); } catch { /* второй отказ — пачку отбрасываем */ }
    return true;
  }
}

/** Отправить всё накопленное пачками по 50. keepalive — отправка на уходе (сворачивание, закрытие). */
export async function flushSponsored(keepalive = false): Promise<void> {
  if (timer) { clearTimeout(timer); timer = null; }
  if (sending || queue.length === 0) return;
  sending = true;
  let offline = false;
  try {
    while (queue.length) {
      const batch = queue.slice(0, BATCH_LIMIT);
      queue = queue.slice(BATCH_LIMIT);
      if (!(await sendBatch(batch, keepalive))) {
        // нет сети — назад в начало очереди (предел 50: старые вытесняются), уйдут со следующей отправкой
        queue = [...batch, ...queue].slice(-BATCH_LIMIT);
        offline = true;
        break;
      }
    }
  } catch { /* не роняем приложение */ } finally {
    sending = false;
    // пришло, пока шла отправка, — своим таймером; без сети — ждём следующей отправки
    if (queue.length && !offline) schedule();
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void flushSponsored(true); });
}

/** Тесты: очистить очередь и таймер. */
export function resetSponsored(): void {
  if (timer) clearTimeout(timer);
  timer = null; queue = []; sending = false;
}
